import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { CharacterCount } from '@/components/CharacterCount';
import { PRODUCT_NAME_MAX } from '@/features/product/field-limits';
import { useServerAutosave } from '@/hooks/useServerAutosave';
import { useIsAdmin } from '@/features/auth/hooks';
import { useAmendProduct, useProductForEdit } from '@/features/product/hooks';
import { archivedProductMessage } from '@/features/product/archived';

/**
 * Product-name editor for the edit page (the section spec view has no name field of its own).
 * Autosaves through the shared `PUT /api/products/{id}` amend (admin gate + audit), and the
 * product LIST table reads `data.product_name`, so a
 * rename shows up there automatically (useAmendProduct invalidates the `['products']` query).
 *
 * Concurrency: writes with `If-Match: "*"` (blind). Safe on this page specifically — `product_type`
 * is immutable, `submission_pathways` is force-restored server-side, and the tech-spec markdown
 * lives in a separate column, so a full-blob write only ever changes `product_name`. This avoids
 * re-pulling the large edit snapshot on every section-markdown save just to keep a token fresh.
 */
export function ProductNameCard({ productId }: { productId: number }) {
  const snapshot = useProductForEdit(productId);
  const amend = useAmendProduct(productId);
  const isAdmin = useIsAdmin();

  // Hydrate once when the snapshot resolves; a later refetch (e.g. after a classification save)
  // must not stomp what the user is currently typing.
  const [name, setName] = useState<string | null>(null);
  useEffect(() => {
    if (snapshot.isSuccess && snapshot.data && name === null) {
      setName((snapshot.data.data.product_name as string | undefined) ?? '');
    }
  }, [snapshot.isSuccess, snapshot.data, name]);

  const autosave = useServerAutosave<string | null>({
    values: name,
    saveFn: async (value) => {
      if (value === null || !snapshot.data) return;
      // amend replaces the whole blob — round-trip the full data with only product_name changed.
      await amend.mutateAsync({ data: { ...snapshot.data.data, product_name: value }, ifMatch: '*' });
    },
    enabled: name !== null && isAdmin,
    debounceMs: 800,
  });

  const productType = typeof snapshot.data?.product_type === 'string' ? snapshot.data.product_type : '';

  return (
    <section className="rounded-2xl border border-line bg-white p-5 shadow-card">
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <label htmlFor="product_name" className="text-[12.5px] font-bold text-navy">
          Product name
        </label>
        <span className="text-[12px] font-medium text-muted" aria-live="polite">
          {autosave.state === 'saving' ? (
            'Saving…'
          ) : autosave.state === 'saved' ? (
            <span className="inline-flex items-center gap-1 text-state-success-text">
              <Check className="size-3.5" aria-hidden /> Saved
            </span>
          ) : autosave.state === 'error' ? (
            // "keep editing to retry" is wrong advice when the product was archived under the
            // user — no retry will ever succeed. Say what the server said instead.
            <span className="text-state-error-text">
              {archivedProductMessage(autosave.lastError) ?? (
                <>Couldn&rsquo;t save &mdash; keep editing to retry</>
              )}
            </span>
          ) : (
            ''
          )}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          id="product_name"
          className="min-w-[240px] flex-1"
          value={name ?? ''}
          disabled={!isAdmin || name === null}
          placeholder="Product name"
          maxLength={PRODUCT_NAME_MAX}
          onChange={(e) => setName(e.target.value)}
        />
        {productType && (
          <span className="inline-flex items-center rounded bg-line-soft px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-navy">
            {productType}
          </span>
        )}
        {/* Only once there is something to count — an empty field needs no "0 / 200". */}
        {name ? <CharacterCount value={name} max={PRODUCT_NAME_MAX} /> : null}
      </div>
      {!isAdmin && (
        <p className="m-0 mt-2 text-[12px] text-muted">Only admins can edit the product name.</p>
      )}
    </section>
  );
}
